import { useGuestSiteWebsiteHeaderSettingsQuery } from './useWebsiteHeaderSettings.graphql.generated';
import { EventDesignPurpose } from '@graphql/generated-base';
import type { DesignLayoutType } from '@graphql/generated-base';
import useWebsiteHeaderUpdatesListener from '../useWebsiteHeaderUpdatesListener/useSubscribeWebsiteHeaderUpdates';
import { isInIframe } from '@shared/utils/isInIframe';
import type {
  AlohaWebsiteHeaderSettings,
  BrannanWebsiteHeaderSettings,
  SupportedAlohaHeaderPhotoLayout,
  SupportedAlohaHeaderPhotoLayoutSchema,
  SupportedBrannanHeaderPhotoLayout,
  SupportedBrannanHeaderPhotoLayoutSchema,
} from '@shared/types/WebsiteHeaderSettings.types';
import { isValidSchema } from './headerSchemaValidator';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { decodeData } from '@shared/utils/encodeData';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';

type UseWebsiteHeaderSettingsArgs = {
  eventHandle: string;
  targetWebsiteLayout: 'aloha' | 'brannan';
  presentationLayoutOverride?: SupportedAlohaHeaderPhotoLayout | SupportedBrannanHeaderPhotoLayout;
  /**
   * Only intended for use in local develoment.
   */
  forceDisable?: boolean;
};

const NO_OP_DEFAULT_RETURN = {
  isLoading: false,
  websiteHeader: null,
};

export const useFetchWebsiteHeaderSettings = (eventHandle: string, skip?: boolean) => {
  const { eventExposure } = useWebsitePublishCapabilityFeature();
  const isPreviewing = isInIframe();
  const shouldUseLivePurpose = eventExposure.value || !isPreviewing;

  const { data, loading: isFetchingWebsiteHeaderSettings } = useGuestSiteWebsiteHeaderSettingsQuery({
    skip: eventExposure.isLoading || skip,
    variables: { eventHandle, eventDesignPurpose: shouldUseLivePurpose ? EventDesignPurpose.live : EventDesignPurpose.draft },
    batchMode: 'off',
    // SSR is required for fixing mobile art rendering issue.
    // We need the value of websiteHeader at the time of first render itself for proper style application by the style applicator.
    ssr: true,
  });

  return {
    data,
    isFetchingWebsiteHeaderSettings,
  };
};

export function useWebsiteHeaderSettings(args: { targetWebsiteLayout: 'aloha'; eventHandle: string }): AlohaWebsiteHeaderSettings;
export function useWebsiteHeaderSettings(args: { targetWebsiteLayout: 'brannan'; eventHandle: string }): BrannanWebsiteHeaderSettings;
export function useWebsiteHeaderSettings(args: UseWebsiteHeaderSettingsArgs): BrannanWebsiteHeaderSettings | AlohaWebsiteHeaderSettings {
  const { targetWebsiteLayout } = args;

  const { data, isFetchingWebsiteHeaderSettings } = useFetchWebsiteHeaderSettings(args.eventHandle);

  const eventData = data?.eventByName;
  const eventDesign = eventData?.eventDesign;
  const activeHeaderPresentationLayout = eventDesign?.activeWebsiteHeaderPresentationLayout;

  // Query param to override the website header settings
  const { templateData } = useQueryParams();
  const decodedTemplateData =
    templateData && typeof templateData === 'string' ? decodeData<{ activeWebsiteHeaderPresentationLayout: typeof activeHeaderPresentationLayout }>(templateData) : null;
  const { overrides } = useWebsiteHeaderUpdatesListener({
    initialValues: {
      activeHeaderPresentationLayout: decodedTemplateData?.activeWebsiteHeaderPresentationLayout || activeHeaderPresentationLayout,
      websiteLayout: (decodedTemplateData?.activeWebsiteHeaderPresentationLayout?.websiteLayout as DesignLayoutType) || eventDesign?.websiteLayout.layoutType,
      eventDisplayName: eventData?.info.eventDisplayName,
      greetingMessage: eventData?.info.greeting,
    },
  });

  if (!eventDesign || !eventData) {
    return {
      isLoading: isFetchingWebsiteHeaderSettings,
      websiteHeader: null,
    };
  }

  let consolidatedActiveHeaderPresentationLayout = overrides.activeHeaderPresentationLayout || activeHeaderPresentationLayout;
  const consolidatedWebsiteLayout = overrides.websiteLayout || eventDesign.websiteLayout.layoutType;
  const consolidatedEventDisplayName = overrides.eventDisplayName || eventData.info.eventDisplayName;
  const consolidatedGreetingMessage = overrides.greetingMessage || eventData.info.greeting;

  if (decodedTemplateData) {
    const activeWebsiteHeaderPresentationLayoutForPreview = decodedTemplateData.activeWebsiteHeaderPresentationLayout;
    if (activeWebsiteHeaderPresentationLayoutForPreview) {
      consolidatedActiveHeaderPresentationLayout = activeWebsiteHeaderPresentationLayoutForPreview;
    }
  }

  if (!consolidatedActiveHeaderPresentationLayout || targetWebsiteLayout !== consolidatedActiveHeaderPresentationLayout.websiteLayout || !consolidatedWebsiteLayout) {
    return {
      isLoading: isFetchingWebsiteHeaderSettings,
      websiteHeader: null,
    };
  }

  // Add this back once API is ready
  // if (!isFetchingWebsiteHeaderSettings && !eventData?.eventDesign?.websiteHeaderPresentationLayout) {
  //  return {
  //    isLoading: false,
  //    websiteHeader: null
  //  };
  // }

  // Need to do an explicit if/else, TSC has difficulty with union type discrimination in this case
  if (
    consolidatedWebsiteLayout === 'aloha' &&
    isValidSchema<SupportedAlohaHeaderPhotoLayoutSchema>(
      consolidatedWebsiteLayout,
      consolidatedActiveHeaderPresentationLayout.schemaURL,
      consolidatedActiveHeaderPresentationLayout.dataJSON,
    )
  ) {
    return {
      websiteHeader: {
        eventDisplayName: consolidatedEventDisplayName,
        greetingMessage: consolidatedGreetingMessage,
        headerPhoto: consolidatedActiveHeaderPresentationLayout.dataJSON,
      },

      isLoading: isFetchingWebsiteHeaderSettings,
    };
  } else if (
    consolidatedWebsiteLayout === 'brannan' &&
    isValidSchema<SupportedBrannanHeaderPhotoLayoutSchema>(
      consolidatedWebsiteLayout,
      consolidatedActiveHeaderPresentationLayout.schemaURL,
      consolidatedActiveHeaderPresentationLayout.dataJSON,
    )
  ) {
    return {
      websiteHeader: {
        eventDisplayName: consolidatedEventDisplayName,
        headerPhoto: consolidatedActiveHeaderPresentationLayout.dataJSON,
      },

      isLoading: isFetchingWebsiteHeaderSettings,
    };
  }

  return NO_OP_DEFAULT_RETURN;
}
